import { colors } from '@creditsights/styleguide-web';
import { UserPermissions } from '@creditsights/api-ui-web';
import { FundamentalAttExtended, FundamentalMetrics } from '../../models/FundamentalMetrics';
import { APIFundamentalAttr } from '../../services/dtos/fundamentalMetrics.dto';
import { NON_FINANCIAL_MODEL_SECTORS } from './RPCalculator.constants';
import {
  CalculatedFundamentals,
  CalculatedFundamentalsTable,
  CalculatedValuesRows,
} from '../../models/CalculatedFundamentals';

enum Period {
  VERY_LOW = 'VERY_LOW',
  LOW = 'LOW',
  MODERATE = 'MODERATE',
  HIGH = 'HIGH',
  VERY_HIGH = 'VERY_HIGH',
  NOT_AVAILABLE = 'NOT_AVAILABLE',
}

const getPeriod = (value: number): Period => {
  switch (true) {
    case value <= 20:
      return Period.VERY_LOW;
    case value > 20 && value <= 40:
      return Period.LOW;
    case value > 40 && value <= 60:
      return Period.MODERATE;
    case value > 60 && value <= 80:
      return Period.HIGH;
    case value > 80:
      return Period.VERY_HIGH;
    default:
      return Period.NOT_AVAILABLE;
  }
};

const cqsColorConfig: Record<Period, string> = {
  [Period.VERY_LOW]: colors.high100,
  [Period.LOW]: colors.high50,
  [Period.MODERATE]: colors.moderate,
  [Period.HIGH]: colors.low50,
  [Period.VERY_HIGH]: colors.low100,
  [Period.NOT_AVAILABLE]: colors.tertiary300,
};

export const getCqsColor = (value: number): string => cqsColorConfig[getPeriod(value)];

const DEFAULT_FRACTION_DIGITS = 6;

export const convertToPercentage = (value: number, fractionDigits = DEFAULT_FRACTION_DIGITS): number =>
  Number((value * 100).toFixed(fractionDigits));

export const prepareDisplayAttributesForCalculation = (
  displayAttributes: FundamentalAttExtended[]
): APIFundamentalAttr[] => {
  const temp: APIFundamentalAttr[] = [];

  displayAttributes.forEach((attr) => {
    const { id, label, value: originalValue } = attr;

    const value = convertToBoolean(attr?.modifiedValue) ? attr.modifiedValue : originalValue;

    temp.push({
      id,
      label,
      // @ts-ignore
      value,
    });
  });

  return temp;
};

export const convertToBoolean = (value: unknown): boolean => {
  return value != null && value !== false;
};

export const canAccess = (userPermissions: UserPermissions, riskOverviewMetrics: FundamentalMetrics): boolean => {
  if (userPermissions && riskOverviewMetrics) {
    const { permissions } = userPermissions;
    const { cqs, modelSector } = riskOverviewMetrics;
    const hasCorrectPermissions =
      permissions.includes('risk_products:risk_calculator') && permissions.includes('risk_products:level_3');
    const hasCorrectCQS = !!cqs;
    const isAllowedSector = NON_FINANCIAL_MODEL_SECTORS.includes(modelSector);

    return hasCorrectCQS && isAllowedSector && hasCorrectPermissions;
  }
  return false;
};

export const mapCalculatedFundamentalsToTable = (
  calculatedFundamentals?: CalculatedFundamentals
): CalculatedFundamentalsTable[] => {
  if (calculatedFundamentals) {
    const { original_values: originalValues, calculated_data: calculatedData, pct } = calculatedFundamentals;

    return CalculatedValuesRows.filter((i) => !i.hidden).map((row) => {
      const { key, tooltip, label } = row;
      return {
        type: key,
        label,
        original: {
          value: getProperty(originalValues, key),
          percentage: getProperty(originalValues, `${key}_pct`),
          tooltip,
        },
        modified: {
          value: getProperty(calculatedData, key),
          percentage: getProperty(pct, `${key}_pct`),
        },
      };
    });
  }
  return [];
};

const getProperty = (obj: any, key: string): number | undefined => {
  if (key in obj) {
    return obj[key];
  }

  return undefined;
};
